/* Settings: staff roster, service list, the scheduling plugin and its service
 * mapping, board options, and branding. */
import * as Vue from 'vue';
import { licensing } from '../licensing.js';
import { store } from '../store.js';
// The same fallback the store applies on save, so a cleared Abbrev box shows the
// letter that will actually replace it rather than the one being cleared.
import { serviceAbbrev } from '../rules/services.js';
import { PeopleSettings } from './people-settings.js';
import { StationSignIn } from './station-signin.js';
import { members } from '../members.js';

/* The two ways round the grid can be drawn — see store.setBoardLayout and the
 * `.board--rows` section of the stylesheet. The wording says what each one puts
 * on which axis rather than naming a shape, because "rows" on its own is
 * ambiguous the moment you are looking at a grid made of both. */
var BOARD_LAYOUTS = [
  { id: 'columns', name: 'A column each', note: 'Turns count down the screen' },
  { id: 'rows', name: 'A row each', note: 'Turns count across the screen' }
];

/* What a live square does with its timer strip — see store.setTurnProgress and
 * `turnPct` in the board. Named for what the square DOES rather than for the
 * shape it makes, because both of these are read at a distance and "bar" on its
 * own says nothing about what it is a bar of. */
var TURN_PROGRESS = [
  { id: 'light', name: 'A blinking light', note: 'Says a service is running' },
  { id: 'bar', name: 'A filling bar', note: 'Says how far through it is' }
];

export const Settings = {
  name: 'Settings',
  components: { StationSignIn: StationSignIn, PeopleSettings: PeopleSettings },
  setup: function () {
    var newName = Vue.ref('');
    var newServices = Vue.ref([]);
    var confirmDeleteId = Vue.ref(null);
    var showArchived = Vue.ref(false);

    var newSvcName = Vue.ref('');
    var newSvcKey = Vue.ref('');
    var newSvcAbbrev = Vue.ref('');
    var newSvcWeight = Vue.ref(1);
    var newSvcMinutes = Vue.ref('');
    var confirmSvcId = Vue.ref(null);

    function toggleNewService(id) {
      var i = newServices.value.indexOf(id);
      if (i === -1) newServices.value.push(id);
      else newServices.value.splice(i, 1);
    }

    function submitNew() {
      if (!store.addStaff(newName.value, newServices.value)) {
        store.toast('Enter a name first.', 'warn');
        return;
      }
      newName.value = '';
      newServices.value = [];
    }

    function submitNewService() {
      var added = store.addService(
        newSvcName.value, newSvcKey.value, newSvcWeight.value, newSvcAbbrev.value,
        newSvcMinutes.value);
      if (!added) {
        store.toast('Enter a service name.', 'warn');
        return;
      }
      newSvcName.value = '';
      newSvcKey.value = '';
      newSvcAbbrev.value = '';
      newSvcWeight.value = 1;
      newSvcMinutes.value = '';
    }

    /* What the square will be drawn with, shown as the abbreviation box's
     * placeholder while it is empty.
     *
     * The key where one has been typed, and the name's first letter where none
     * has — the same two-step fallback the store applies on save, so the grey
     * letter in the box is the letter that will actually appear on the board.
     * It used to be the key's placeholder too, which read as a suggestion but
     * behaved as a default: leaving the box empty took the letter anyway, and
     * that is exactly what made the hotkey compulsory. The key box now shows the
     * dash it shares with Minutes, because empty there is a real answer.
     */
    var suggestedAbbrev = Vue.computed(function () {
      // Nothing typed yet is nothing to suggest — the rule's last resort is a
      // `?`, which belongs on a square rather than in an empty form.
      if (!newSvcName.value.trim() && !newSvcKey.value.trim()) return '';
      return serviceAbbrev('', newSvcName.value, newSvcKey.value);
    });

    // And the same answer for a service that already exists: what the square
    // falls back to if its Abbrev box is emptied.
    function fallbackAbbrev(svc) {
      return serviceAbbrev('', svc.name, svc.shortcut);
    }

    function doDelete(staffId) {
      store.deleteStaff(staffId);
      confirmDeleteId.value = null;
    }

    function doDeleteService(serviceId) {
      store.deleteService(serviceId);
      confirmSvcId.value = null;
    }

    var confirmReset = Vue.ref(false);

    function doReset() {
      store.resetToday();
      confirmReset.value = false;
    }

    /* ---------- self check-in ----------
     *
     * The wording fields as the screen presents them: grouped by which of the
     * check-in screens the line appears on, because that is how somebody
     * editing them thinks — "the one it says when nobody is free" — rather than
     * as eighteen boxes in a column. The grouping is data on each field (see
     * js/rules/checkin-look.js) rather than a second list here, so adding a
     * line is one entry in one file.
     */
    var textGroups = Vue.computed(function () {
      var out = [];
      var byName = {};
      store.checkinTextFields.forEach(function (f) {
        if (!byName[f.group]) {
          byName[f.group] = { name: f.group, fields: [] };
          out.push(byName[f.group]);
        }
        byName[f.group].fields.push(f);
      });
      return out;
    });

    /* The check-in service list, cut into columns of five.
     *
     * A salon with two dozen services was getting a single column two dozen
     * switches deep — a card taller than the window with the rest of the tab
     * pushed off the bottom of it, and nothing to scan against. Five is short
     * enough to take in without counting and long enough that a normal list is
     * two or three columns rather than eight.
     *
     * Cut here rather than left to the stylesheet because CSS cannot do it: a
     * grid can fix the number of ROWS and flow sideways, but then the columns
     * never wrap and a long list runs off the side of the card instead. Real
     * columns in the markup can be laid out as ordinary grid cells, so they
     * wrap onto a second band when the card runs out of width.
     */
    var CHECKIN_SVCS_PER_COL = 5;

    var checkinSvcCols = Vue.computed(function () {
      var out = [];
      store.activeServices.value.forEach(function (svc, i) {
        if (i % CHECKIN_SVCS_PER_COL === 0) out.push([]);
        out[out.length - 1].push(svc);
      });
      return out;
    });

    var confirmResetLook = Vue.ref(false);

    function doResetLook() {
      store.resetCheckinLook();
      confirmResetLook.value = false;
    }

    /* ---------- booking instead of waiting ----------
     *
     * The switch and the box are one setting, the same way the Due now lead is:
     * null is "no cutoff" and a number is one, so the switch writes the default
     * back and clearing it writes null. Read off the stored value rather than
     * held as a second flag, so a station that had this open when another station
     * changed it comes up on the right control.
     */
    function setBookCutoff(on) {
      store.setCheckinBookAfter(on ? store.checkinBookDefault : null);
    }

    // Typed, not chosen. Nothing is written until it is a number, so a box on its
    // way from 60 to 90 does not clamp itself to the floor as it passes through
    // being empty — the same reason `onDueSoonInput` reads the way it does.
    function onBookCutoffInput(value) {
      if (String(value).trim() === '') return;
      var n = Number(value);
      if (!isFinite(n) || n < store.checkinBookLimits.min) return;
      store.setCheckinBookAfter(n);
    }

    /* The link as it will actually be used, for one real service, shown under the
     * box while it is being set up.
     *
     * The first service on the tablet with a booking id filled in, and the first
     * on it at all when none have one — so the example is the interesting case
     * where there is one, and the honest "this is where everybody lands" when
     * there is not. Through `store.checkinBookingLink`, which is the same call the
     * tablet makes, so this cannot show one thing and send a client to another.
     */
    var bookExample = Vue.computed(function () {
      var list = store.checkinServices.value;
      if (!list.length || !store.checkinBookingUrl.value) return null;
      var svc = list.find(function (s) { return s.bookingServiceId; }) || list[0];
      return { name: svc.name, url: store.checkinBookingLink(svc.id) };
    });

    // How many of the services on the tablet have an id to put in the link. What
    // the count under the list is for: with a placeholder in the link, the ones
    // without an id all land on the booking site's front page instead.
    var bookIdCount = Vue.computed(function () {
      return store.checkinServices.value.filter(function (s) {
        return !!s.bookingServiceId;
      }).length;
    });

    /* Worked out rather than written down, because "checkin.html" is only half
     * an answer to somebody standing at a tablet with an address bar in front
     * of them. It is the console's own URL with the last path segment swapped,
     * so it is right at /dash/, at the root of a subdomain, and under whatever
     * folder the build was copied into — the same path-agnosticism the rest of
     * dist/ has. */
    var checkinUrl = Vue.computed(function () {
      try {
        var u = new URL('checkin.html', window.location.href);
        return u.host + u.pathname;
      } catch (e) {
        return 'checkin.html';
      }
    });

    /* ---------- how early a booking joins Due now ----------
     *
     * Three buttons and a box. Which of the two is showing is read off the
     * stored value rather than held as its own flag, so a station whose number
     * was set from another station comes up on the right control — a lead of
     * seven set at the desk must not read as "10" with the box hidden on the
     * wall screen.
     *
     * `custom` only ever forces the box OPEN for a value that is one of the
     * three; it is never what decides that a stored 7 is custom. Otherwise
     * pressing Custom and typing 5 would leave the box on screen showing a
     * number the buttons also claim.
     */
    var dueCustom = Vue.ref(false);

    var dueIsCustom = Vue.computed(function () {
      return dueCustom.value ||
        store.dueSoonChoices.indexOf(store.dueSoonMinutes.value) === -1;
    });

    function pickDueSoon(minutes) {
      dueCustom.value = false;
      store.setDueSoonMinutes(minutes);
    }

    // Typed, not chosen: an empty or half-typed box would otherwise clamp
    // itself to 1 on the way to 15. Nothing is written until it is a number.
    function onDueSoonInput(value) {
      if (String(value).trim() === '') return;
      var n = Number(value);
      if (!isFinite(n) || n < store.dueSoonLimits.min) return;
      store.setDueSoonMinutes(n);
    }

    /* ---------- conflict buffer ----------
     *
     * The same three-buttons-and-Custom as Due now above, for the same reasons.
     * Zero is one of the buttons rather than something only typed: it is the
     * strict reading, and a salon that wants it should not have to know that
     * the box takes it.
     */
    var bufferCustom = Vue.ref(false);

    var bufferIsCustom = Vue.computed(function () {
      return bufferCustom.value ||
        store.conflictBufferChoices.indexOf(store.conflictBufferMinutes.value) === -1;
    });

    function pickBuffer(minutes) {
      bufferCustom.value = false;
      store.setConflictBufferMinutes(minutes);
    }

    function onBufferInput(value) {
      if (String(value).trim() === '') return;
      var n = Number(value);
      if (!isFinite(n) || n < store.conflictBufferLimits.min) return;
      store.setConflictBufferMinutes(n);
    }

    /* ---------- business hours ----------
     *
     * `<input type="time">` rather than a text box: it hands back "HH:MM" or
     * nothing at all, which is the whole of the validation the store would
     * otherwise be doing on half-typed strings. `change` rather than `input`,
     * so a write does not go up once per digit as the field is filled in.
     */
    var clearHours = Vue.ref(false);

    function doClearHours() {
      store.clearBusinessHours();
      clearHours.value = false;
    }

    // Read back in plain English so a mistyped 7 AM close is visible as one.
    function hoursLabel(row) {
      if (row.closed) return 'Closed';
      if (!row.open || !row.close) return 'Not set';
      return store.formatClock(store.clockAt(row.open)) + ' – ' +
        store.formatClock(store.clockAt(row.close));
    }

    // Today's row, so the day the salon is actually in is the one that reads
    // as the live setting rather than as the fourth line down.
    var todayDow = Vue.computed(function () {
      var t = store.todayHours.value;
      if (t) return t.dow;
      var p = String(store.state.businessDate).split('-');
      return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2])).getDay();
    });

    function openScheduling() {
      store.ui.configTab = 'scheduling';
      if (store.schedulingOn.value && !store.appts.catalogueLoaded) store.loadCatalogue();
      if (store.schedulingOn.value) store.loadOptionCatalogue();
    }

    // Options that match the search, grouped as the booking system groups
    // them. A group with nothing left in it drops out rather than sitting
    // there empty.
    var filteredOptions = Vue.computed(function () {
      var q = store.appts.optionFilter.trim().toLowerCase();
      return store.optionGroups.value.map(function (g) {
        return {
          name: g.name,
          options: g.options.filter(function (o) {
            return !q || o.name.toLowerCase().indexOf(q) !== -1 ||
              g.name.toLowerCase().indexOf(q) !== -1;
          })
        };
      }).filter(function (g) { return g.options.length; });
    });

    function ruleOf(option) { return store.optionRuleFor(option.id) || {}; }
    function ruleStaff(option) { return ruleOf(option).staffIds || []; }

    // Loading the catalogue is what makes the mapping list appear, so switching
    // the plugin on from this tab has to fetch it too.
    function onSchedulingToggle(on, el) {
      store.requestScheduling(on);
      // Switching off with a mapping stored only opens a confirmation — nothing
      // has changed yet, so put the switch back where the setting actually is.
      // It flips for real when the modal is answered.
      if (el) el.checked = store.scheduling.wanted.value;
      if (on) Vue.nextTick(function () { store.loadCatalogue(); });
    }

    var mappedCount = Vue.computed(function () {
      return Object.keys(store.appts.serviceMap).filter(function (k) {
        return store.appts.serviceMap[k];
      }).length;
    });

    var hasCustomColors = Vue.computed(function () {
      return Object.keys(store.theme.custom).length > 0;
    });

    // Cleared afterwards so picking the same file twice still fires a change.
    function onLogoPick(evt) {
      var file = evt.target.files && evt.target.files[0];
      if (file) store.setLogo(file);
      evt.target.value = '';
    }

    var filteredCatalogue = Vue.computed(function () {
      var q = store.appts.mapFilter.trim().toLowerCase();
      return store.appts.catalogue.filter(function (m) {
        if (store.appts.unmappedOnly && store.appts.serviceMap[m.id]) return false;
        return !q || String(m.name).toLowerCase().indexOf(q) !== -1;
      });
    });

    /* ---------- the service map, by what things count as ----------
     *
     * There are a hundred and fifty-odd services in the booking system and
     * eleven turn types. Drawn as a chip per type per service, that was some
     * two thousand buttons on one page and no way to see what was left to do.
     *
     * So it is turned around: the page is a list of the turn types, each one
     * holding what counts as it, with everything still unmapped at the top and
     * open. The choice per row is one control instead of twelve. What you came
     * to the page to find — the unmapped ones — is the first thing on it, and
     * what you came to check — what counts as a Pedicure — is one heading.
     */
    var openGroups = Vue.reactive({});

    // A search is a jump to a name, so it opens everything: a hit inside a
    // collapsed group would otherwise look like no hit at all.
    function groupOpen(key) {
      if (store.appts.mapFilter.trim()) return true;
      if (openGroups[key] != null) return openGroups[key];
      return key === 'none';
    }

    function toggleGroup(key) { openGroups[key] = !groupOpen(key); }

    var mapGroups = Vue.computed(function () {
      var byType = { none: [] };
      filteredCatalogue.value.forEach(function (m) {
        var id = store.appts.serviceMap[m.id];
        var key = id && store.serviceById.value[id] ? id : 'none';
        (byType[key] || (byType[key] = [])).push(m);
      });

      var out = [{
        key: 'none', name: 'Not mapped yet', letter: '?',
        color: store.defaultServiceColor, items: byType.none, lead: true
      }];
      store.activeServices.value.forEach(function (svc) {
        out.push({
          key: svc.id, name: svc.name, letter: svc.abbrev,
          color: svc.color, items: byType[svc.id] || []
        });
      });
      // A turn type nothing maps to is still worth a line — it says the type
      // exists and nothing counts as it — but only while the list is whole.
      // Filtered, an empty group is just noise.
      var filtered = !!store.appts.mapFilter.trim() || store.appts.unmappedOnly;
      return out.filter(function (g) { return g.items.length || (!filtered && !g.lead); });
    });

    // Every option's rule is edited one at a time, in place. Open, it is the
    // same two questions as before; closed, it is a line of text saying what
    // the option does, which is what you read the page for.
    var openOptionId = Vue.ref(null);

    function toggleOption(id) {
      openOptionId.value = openOptionId.value === id ? null : id;
    }

    // The turn types this option can be booked on, by name — one row each in
    // the editor, so an option on manicures and pedicures can say what it
    // makes of each. js/rules/option-type.js.
    function optionBases(o) {
      return (o.baseServiceIds || []).map(function (id) {
        return store.serviceById.value[id];
      }).filter(Boolean).sort(function (a, b) { return a.sortOrder - b.sortOrder; });
    }

    function ruleBase(o, baseId) {
      var r = store.optionRuleFor(o.id);
      return (r && r.byBase && r.byBase[baseId]) || '';
    }

    function optionSummary(o) {
      var r = store.optionRuleFor(o.id);
      var byBase = (r && r.byBase) || {};
      if (!r || (!r.serviceId && !(r.staffIds || []).length && !Object.keys(byBase).length)) {
        return 'No effect';
      }
      var bits = [];
      Object.keys(byBase).forEach(function (baseId) {
        var from = store.serviceById.value[baseId], to = store.serviceById.value[byBase[baseId]];
        if (from && to) bits.push('a ' + from.name + ' counts as ' + to.name);
      });
      var svc = r.serviceId && store.serviceById.value[r.serviceId];
      if (svc) bits.push((bits.length ? 'otherwise ' : '') + 'counts as ' + svc.name);
      var ids = r.staffIds || [];
      if (ids.length) {
        var names = ids.map(function (id) {
          var s = store.staffOf(id);
          return s ? s.name : 'removed';
        });
        bits.push(names.length > 2
          ? names.length + ' technicians only'
          : names.join(' and ') + ' only');
      }
      return bits.join(' · ');
    }

    return {
      auth: store.auth,
      appts: store.appts,
      confirmReset: confirmReset,
      doReset: doReset,
      signOut: store.signOut,
      setShowNextUp: store.setShowNextUp,
      setBoardLayout: store.setBoardLayout,
      boardLayouts: BOARD_LAYOUTS,
      setTurnProgress: store.setTurnProgress,
      turnProgressModes: TURN_PROGRESS,
      setCheckinWaits: store.setCheckinWaits,
      // ---- self check-in ----
      checkinLicensed: store.checkinLicensed,
      checkinServices: store.checkinServices,
      checkinUrl: checkinUrl,
      look: store.checkinLook,
      // What has been OVERRIDDEN, which is what the boxes show. Bound to the
      // resolved values instead, every box would look filled in and clearing
      // one would read as deleting a colour somebody had chosen.
      rawLook: store.checkinLookRaw,
      checkinCustomised: store.checkinCustomised,
      setCheckinLook: store.setCheckinLook,
      checkinSvcCols: checkinSvcCols,
      colorFields: store.checkinColorFields,
      textGroups: textGroups,
      textMax: store.checkinTextMax,
      confirmResetLook: confirmResetLook,
      doResetLook: doResetLook,
      // ---- booking instead of waiting ----
      bookingUrl: store.checkinBookingUrl,
      bookingPerService: store.checkinBookingPerService,
      bookAfter: store.checkinBookAfter,
      bookLimits: store.checkinBookLimits,
      bookPlaceholder: store.checkinBookPlaceholder,
      bookRefMax: store.checkinBookingRefMax,
      setBookingUrl: store.setCheckinBookingUrl,
      setBookCutoff: setBookCutoff,
      onBookCutoffInput: onBookCutoffInput,
      bookExample: bookExample,
      bookIdCount: bookIdCount,
      setRequestsOn: store.setRequestsOn,
      dueSoonMinutes: store.dueSoonMinutes,
      dueSoonChoices: store.dueSoonChoices,
      dueSoonLimits: store.dueSoonLimits,
      conflictBufferMinutes: store.conflictBufferMinutes,
      conflictBufferChoices: store.conflictBufferChoices,
      conflictBufferLimits: store.conflictBufferLimits,
      bufferCustom: bufferCustom,
      bufferIsCustom: bufferIsCustom,
      pickBuffer: pickBuffer,
      onBufferInput: onBufferInput,
      dueIsCustom: dueIsCustom,
      dueCustom: dueCustom,
      pickDueSoon: pickDueSoon,
      onDueSoonInput: onDueSoonInput,
      setAutoStop: store.setAutoStop,
      setAutoStart: store.setAutoStart,
      hoursRows: store.hoursRows,
      hoursSet: store.hoursSet,
      todayHours: store.todayHours,
      todayDow: todayDow,
      openLead: store.openLeadMinutes,
      closingGrace: store.closingGraceMinutes,
      // In hours, because that is how the window is described and two is a
      // rounder thing to read than a hundred and twenty.
      lastFinishWindow: store.lastFinishWindowMinutes / 60,
      setDayHours: store.setDayHours,
      copyHours: store.copyHoursToOpenDays,
      clearHours: clearHours,
      doClearHours: doClearHours,
      hoursLabel: hoursLabel,
      formatClock: store.formatClock,
      theme: store.theme,
      themePresets: store.themePresets,
      themeFields: store.themeFields,
      themeValue: store.themeValue,
      setThemePreset: store.setThemePreset,
      setThemeColor: store.setThemeColor,
      clearThemeCustom: store.clearThemeCustom,
      hasCustomColors: hasCustomColors,
      logo: store.logo,
      logoLimits: store.logoLimits,
      fileSizeLabel: store.fileSizeLabel,
      brand: store.brand,
      brandLimits: store.brandLimits,
      businessName: store.businessName,
      setBusinessName: store.setBusinessName,
      onLogoPick: onLogoPick,
      clearLogo: store.clearLogo,
      openScheduling: openScheduling,
      onSchedulingToggle: onSchedulingToggle,
      scheduled: store.schedulingOn,
      schedulingWanted: store.scheduling.wanted,
      schedulingName: store.schedulingName,
      schedulingPlugins: store.schedulingPlugins,
      schedulingPlugin: store.schedulingPlugin,
      setSchedulingPlugin: store.setSchedulingPlugin,

      /* Whether either slot is on this salon's plan at all.
       *
       * A slot that is off because nobody switched it on and a slot that is off
       * because it has not been bought are the same `enabled` value and want
       * completely different screens — the first has a switch that will help
       * and the second does not. So the panes ask this before they draw a
       * control that could not do anything. On a console with no licensing
       * behind it both are true, always. */
      schedulingLicensed: store.scheduling.licensed,
      licence: licensing.summary,

      canCheckout: Vue.computed(function () { return store.schedulingCan('checkout'); }),
      canCatalogue: Vue.computed(function () { return store.schedulingCan('catalogue'); }),
      mappedServiceIds: store.mappedServiceIds,
      mappedCount: mappedCount,
      filteredCatalogue: filteredCatalogue,
      mapGroups: mapGroups,
      groupOpen: groupOpen,
      toggleGroup: toggleGroup,
      openOptionId: openOptionId,
      toggleOption: toggleOption,
      optionSummary: optionSummary,
      mapExternalService: store.mapExternalService,
      filteredOptions: filteredOptions,
      ruleOf: ruleOf,
      ruleStaff: ruleStaff,
      optionRuleCount: store.optionRuleCount,
      setOptionRuleService: store.setOptionRuleService,
      setOptionRuleBase: store.setOptionRuleBase,
      optionBases: optionBases,
      ruleBase: ruleBase,
      toggleOptionRuleStaff: store.toggleOptionRuleStaff,
      canOptions: Vue.computed(function () { return store.schedulingCan('options'); }),
      ui: store.ui,
      state: store.state,
      services: store.activeServices,
      roster: store.rosterStaff,
      archived: store.archivedStaff,
      duplicateShortcuts: store.duplicateShortcuts,
      palette: store.PALETTE,
      noColor: store.defaultServiceColor,
      newName: newName, newServices: newServices, confirmDeleteId: confirmDeleteId,
      showArchived: showArchived,
      newSvcName: newSvcName, newSvcKey: newSvcKey, newSvcAbbrev: newSvcAbbrev,
      newSvcWeight: newSvcWeight, newSvcMinutes: newSvcMinutes,
      turnWeights: store.turnWeights,
      minMinutes: store.MIN_SERVICE_MINUTES,
      maxMinutes: store.MAX_SERVICE_MINUTES,
      weightLabel: store.weightLabel,
      confirmSvcId: confirmSvcId, suggestedAbbrev: suggestedAbbrev,
      fallbackAbbrev: fallbackAbbrev,
      toggleNewService: toggleNewService,
      submitNew: submitNew,
      submitNewService: submitNewService,
      doDelete: doDelete,
      doDeleteService: doDeleteService,
      // Whether this session is an owner of this salon. Read off the members
      // list rather than asked separately, so the tab and the buttons inside it
      // cannot disagree — see js/members.js.
      youAreOwner: members.youAreOwner,
      updateStaff: store.updateStaff,
      toggleStaffService: store.toggleStaffService,
      restoreStaff: store.restoreStaff,
      updateService: store.updateService,
      reorderService: store.reorderService
    };
  },
  template: [
    '<div class="config">',
    '  <div class="tabs">',
    '    <button type="button" class="tab" :class="{ \'is-on\': ui.configTab === \'staff\' }"',
    '      @click="ui.configTab = \'staff\'">Staff</button>',
    '    <button type="button" class="tab" :class="{ \'is-on\': ui.configTab === \'services\' }"',
    '      @click="ui.configTab = \'services\'">Services</button>',
    '    <button type="button" class="tab" :class="{ \'is-on\': ui.configTab === \'scheduling\' }"',
    '      @click="openScheduling">Appointments',
    '      <span class="tab__dot" :class="scheduled ? \'is-on\' : \'is-off\'"',
    '        :title="scheduled ? schedulingName + \' is connected\' : \'No booking system connected\'"></span></button>',
    '    <button type="button" class="tab" :class="{ \'is-on\': ui.configTab === \'board\' }"',
    '      @click="ui.configTab = \'board\'">Board &amp; day</button>',
    /* Listed whether or not the salon is licensed for it, and marked when they
       are not — the same rule the module switcher follows in js/apps.js, for
       the same reason stated there: a menu that silently loses an entry is a
       product nobody can ask about, where one that says "not on your plan" is a
       phone call. */
    '    <button type="button" class="tab" :class="{ \'is-on\': ui.configTab === \'checkin\' }"',
    '      @click="ui.configTab = \'checkin\'">Check-in',
    '      <span v-if="!checkinLicensed" class="tab__lock" aria-label="Not on this plan"',
    '        title="Not part of this subscription">&#128274;</span></button>',
    '    <button type="button" class="tab" :class="{ \'is-on\': ui.configTab === \'theme\' }"',
    '      @click="ui.configTab = \'theme\'">Branding</button>',
    /* Owners only, and hidden rather than locked — unlike Check-in beside it,
       which is a feature this salon could buy. This is not a plan they are
       missing, it is a job that is not theirs: the front desk has no more use
       for a padlocked People tab than for a padlocked payroll. */
    '    <button v-if="youAreOwner" type="button" class="tab"',
    '      :class="{ \'is-on\': ui.configTab === \'people\' }"',
    '      @click="ui.configTab = \'people\'">People</button>',
    '  </div>',
    '',
    /* ---------------- staff ---------------- */
    '  <template v-if="ui.configTab === \'staff\'">',
    '    <div class="config__intro">',
    '      <h2>Staff</h2>',
    /* The roster is who works here and what they do — facts that outlive the
       day. Who is in, what order the columns are in and how many turns each of
       them has are facts about TODAY, and they are managed where today is: on
       the board. Two lists of the same names, one of them stale by lunchtime,
       was the clutter this tab is missing. */
    '      <p><span>Who works here, and which services each of them performs. Everything about <strong>today</strong> &mdash; who is in, what order the columns are in, how many turns each of them has &mdash; is on the board itself.</span></p>',
    '      <p class="config__where">On the board: the <strong>+</strong> past the last column marks people in for the day &middot; drag a name to reorder &middot; right-click a name to skip someone or take them off.</p>',
    '    </div>',
    '',
    '    <form class="add-card" @submit.prevent="submitNew">',
    '      <h3>Add a technician</h3>',
    '      <div class="add-card__row">',
    '        <input v-model="newName" type="text" class="input" placeholder="Name" maxlength="40" />',
    '        <button type="submit" class="btn btn--primary">Add</button>',
    '      </div>',
    '      <div class="svc-toggles">',
    '        <button v-for="svc in services" :key="svc.id" type="button" class="chip chip--sm"',
    '          :class="{ \'chip--on\': newServices.includes(svc.id) }" :style="{ \'--chip\': svc.color }"',
    '          @click="toggleNewService(svc.id)">',
    /* The abbreviation, which is what the board draws and is never empty — not
       the key, which is optional and would leave a blank disc on the chip. */
    '          <span class="chip__letter">{{ svc.abbrev }}</span>',
    '          <span class="chip__name">{{ svc.name }}</span>',
    '        </button>',
    '      </div>',
    '    </form>',
    '',
    '    <p v-if="!roster.length" class="config__empty">No technicians yet.</p>',
    '',
    '    <ul class="staff-list staff-list--roster">',
    '      <li v-for="s in roster" :key="s.id" class="staff-row">',
    '        <div class="staff-row__main">',
    '          <input class="input input--name" type="text" :value="s.name" maxlength="40"',
    '            @change="updateStaff(s.id, { name: $event.target.value })" />',
    /* Who they usually are. The desk can change it for one day from the
       right-click on their name; this is what every other day starts from. */
    '          <label class="switch switch--sm" title="On the board for clients who ask for them, out of the rotation for everybody else">',
    '            <input type="checkbox" :checked="!!s.ownClientsOnly"',
    '              @change="updateStaff(s.id, { ownClientsOnly: $event.target.checked })" />',
    '            <span class="switch__track"><span class="switch__thumb"></span></span>',
    '            <span class="switch__label">Own clients only</span>',
    '          </label>',
    '          <div class="svc-toggles">',
    '            <button v-for="svc in services" :key="svc.id" type="button" class="chip chip--sm"',
    '              :class="{ \'chip--on\': s.serviceIds.includes(svc.id) }" :style="{ \'--chip\': svc.color }"',
    '              @click="toggleStaffService(s.id, svc.id)">',
    '              <span class="chip__letter">{{ svc.abbrev }}</span>',
    '              <span class="chip__name">{{ svc.name }}</span>',
    '            </button>',
    '          </div>',
    '        </div>',
    '',
    '        <div class="staff-row__delete">',
    '          <button v-if="confirmDeleteId !== s.id" type="button" class="btn btn--danger-ghost btn--sm"',
    '            @click="confirmDeleteId = s.id">Remove</button>',
    '          <template v-else>',
    '            <button type="button" class="btn btn--danger btn--sm" @click="doDelete(s.id)">',
    '              Remove (keeps history)</button>',
    '            <button type="button" class="btn btn--quiet btn--sm" @click="confirmDeleteId = null">',
    '              Cancel</button>',
    '          </template>',
    '        </div>',
    '      </li>',
    '    </ul>',
    '',
    '    <div v-if="archived.length" class="archived">',
    '      <button type="button" class="link-btn" @click="showArchived = !showArchived">',
    '        {{ showArchived ? "Hide" : "Show" }} removed staff ({{ archived.length }})</button>',
    '      <ul v-if="showArchived" class="archived__list">',
    '        <li v-for="s in archived" :key="s.id">',
    '          <span>{{ s.name }}</span>',
    '          <button type="button" class="btn btn--quiet btn--sm" @click="restoreStaff(s.id)">Restore</button>',
    '        </li>',
    '      </ul>',
    '    </div>',
    '  </template>',
    '',
    /* ---------------- services ---------------- */
    '  <template v-else-if="ui.configTab === \'services\'">',
    '    <div class="config__intro">',
    '      <h2>Services</h2>',
    '      <p><span>The <strong>key</strong> is what you press to book that service, and it is <strong>optional</strong> &mdash; leave it empty, or clear it later, and the service is simply not on the keyboard; everywhere else it is booked exactly as before. The <strong>abbreviation</strong> is what gets drawn in the square and on every badge &mdash; up to two characters, and it need not match the key. Leave it empty and it follows the key, or the name where there is no key. <strong>Next availability</strong> is whether it gets a row in the board&rsquo;s top bar &mdash; turn it off for the rarely booked ones; they stay bookable everywhere else. Each service counts as a <strong>full</strong>, <strong>half</strong> or <strong>zero</strong> turn against a tech\'s place in the queue &mdash; zero for work that should not cost them their turn, like a nail fix. </span><span v-if="ui.requestsOn">A client request does not change any of this &mdash; it stars the square and nothing more, so a half-turn service asked for by name is still a half turn. </span><span><strong>Minutes</strong> is how long the type usually takes: set it and a walk-in square counts down against it. Leave it empty and the square just counts up, as it always has. A booking&rsquo;s own length always wins &mdash; the default is only what to go on when there is no appointment behind the square.</span></p>',
    '    </div>',
    '',
    '    <form class="add-card" @submit.prevent="submitNewService">',
    '      <h3>Add a service</h3>',
    '      <div class="add-card__row add-card__row--fields">',
    '        <label class="field field--grow">',
    '          <span class="field__label">Service</span>',
    '          <input v-model="newSvcName" type="text" class="input" placeholder="Service name" maxlength="30" />',
    '        </label>',
    /* The same three labels the rows below carry, and the two one-character
       boxes are told apart by their placeholders: a dash on the optional one,
       the letter it will be drawn with on the one that always has an answer. */
    '        <label class="field">',
    '          <span class="field__label">Key</span>',
    /* A dash, the same as Minutes — because leaving it empty is a real answer
       and the service simply is not on the keyboard. This used to show the
       name's first letter, which read as a suggestion and behaved as a default:
       there was no way to add a service without a hotkey. */
    '          <input v-model="newSvcKey" type="text" class="input input--key" maxlength="1"',
    '            placeholder="&mdash;" title="Hotkey — optional" aria-label="Shortcut key" />',
    '        </label>',
    '        <label class="field">',
    '          <span class="field__label">Abbrev</span>',
    '          <input v-model="newSvcAbbrev" type="text" class="input input--key input--abbrev" maxlength="2"',
    '            :placeholder="suggestedAbbrev" title="Drawn on the square" aria-label="Abbreviation" />',
    '        </label>',
    '        <label class="field">',
    '          <span class="field__label">Minutes</span>',
    '          <input v-model="newSvcMinutes" type="number" class="input input--mins"',
    '            :min="minMinutes" :max="maxMinutes" step="5"',
    '            placeholder="Mins" title="How long it usually takes — optional" aria-label="Default length in minutes" />',
    '        </label>',
    '        <div class="weight-pick" role="radiogroup" aria-label="Counts as">',
    '          <button v-for="w in turnWeights" :key="w" type="button" class="weight-pick__opt"',
    '            :class="{ \'is-on\': newSvcWeight === w }" role="radio"',
    '            :aria-checked="newSvcWeight === w ? \'true\' : \'false\'"',
    '            @click="newSvcWeight = w">{{ weightLabel(w) }}</button>',
    '        </div>',
    '        <button type="submit" class="btn btn--primary">Add</button>',
    '      </div>',
    '    </form>',
    '',
    '    <ul class="staff-list">',
    '      <li v-for="(svc, i) in services" :key="svc.id" class="staff-row">',
    '        <div class="staff-row__order">',
    '          <button type="button" class="icon-btn" :disabled="i === 0" @click="reorderService(i, i - 1)"',
    '            aria-label="Move up">&#9650;</button>',
    '          <span class="staff-row__pos">{{ i + 1 }}</span>',
    '          <button type="button" class="icon-btn" :disabled="i === services.length - 1"',
    '            @click="reorderService(i, i + 1)" aria-label="Move down">&#9660;</button>',
    '        </div>',
    '        <div class="staff-row__main">',
    '          <div class="staff-row__top">',
    '            <span class="svc-swatch" :style="{ background: svc.color }">{{ svc.abbrev }}</span>',
    '            <input class="input input--name" type="text" :value="svc.name" maxlength="30"',
    '              @change="updateService(svc.id, { name: $event.target.value })" />',
    /* Clearing this box is how a service comes off the keyboard, so the
       placeholder is the dash the empty state deserves rather than a letter
       implying one is still bound. */
    '            <label class="field">',
    '              <span class="field__label">Key</span>',
    '              <input class="input input--key" type="text" :value="svc.shortcut" maxlength="1"',
    '                placeholder="&mdash;" title="Hotkey &mdash; optional"',
    '                :class="{ \'input--bad\': duplicateShortcuts[svc.shortcut] }"',
    '                @change="updateService(svc.id, { shortcut: $event.target.value })" />',
    '            </label>',
    '            <label class="field">',
    '              <span class="field__label">Abbrev</span>',
    '              <input class="input input--key input--abbrev" type="text" :value="svc.abbrev"',
    '                maxlength="2" :placeholder="fallbackAbbrev(svc)"',
    '                @change="updateService(svc.id, { abbrev: $event.target.value })" />',
    '            </label>',
    /* Blank is a real answer — no countdown — so the placeholder says so
       rather than suggesting a number nobody chose. */
    '            <label class="field">',
    '              <span class="field__label">Minutes</span>',
    '              <input class="input input--mins" type="number"',
    '                :min="minMinutes" :max="maxMinutes" step="5"',
    '                :value="svc.defaultMinutes == null ? \'\' : svc.defaultMinutes"',
    '                placeholder="—" title="How long this usually takes. A booking\'s own length always wins."',
    '                @change="updateService(svc.id, { defaultMinutes: $event.target.value })" />',
    '            </label>',
    /* Which side of the client it is. A Feet service on a visit may be started
       alongside a Hands one by a second technician; see js/rules/same-time.js. */
    '            <label class="field">',
    '              <span class="field__label">Same time</span>',
    '              <select class="input input--mins" :value="svc.sameTime || \'\'"',
    '                title="Feet services (pedicures) can be done at the same time as Hands services (manicures, sets, refills) on one visit. Off never runs alongside anything."',
    '                @change="updateService(svc.id, { sameTime: $event.target.value })">',
    '                <option value="">Off</option>',
    '                <option value="feet">Feet</option>',
    '                <option value="hands">Hands</option>',
    '              </select>',
    '            </label>',
    '            <label class="switch switch--sm">',
    '              <input type="checkbox" :checked="svc.quickAdd !== false"',
    '                @change="updateService(svc.id, { quickAdd: $event.target.checked })" />',
    '              <span class="switch__track"><span class="switch__thumb"></span></span>',
    '              <span class="switch__label">Next availability</span>',
    '            </label>',
    '            <div class="weight-pick" role="radiogroup" :aria-label="svc.name + \' counts as\'">',
    '              <button v-for="w in turnWeights" :key="w" type="button" class="weight-pick__opt"',
    '                :class="{ \'is-on\': svc.turnWeight === w }" role="radio"',
    '                :aria-checked="svc.turnWeight === w ? \'true\' : \'false\'"',
    '                @click="updateService(svc.id, { turnWeight: w })">{{ weightLabel(w) }}</button>',
    '            </div>',
    '          </div>',
    '          <div class="swatches">',
    '            <button v-for="c in palette" :key="c" type="button" class="swatch"',
    '              :class="{ \'is-on\': svc.color === c }" :style="{ background: c }"',
    '              :aria-label="\'Set colour \' + c" @click="updateService(svc.id, { color: c })"></button>',
    '          </div>',
    '          <p v-if="duplicateShortcuts[svc.shortcut]" class="warn-line">',
    '            Key <strong>{{ svc.shortcut }}</strong> is used more than once &mdash; only the first service responds to it.</p>',
    '        </div>',
    '        <div class="staff-row__delete">',
    '          <button v-if="confirmSvcId !== svc.id" type="button" class="btn btn--danger-ghost btn--sm"',
    '            @click="confirmSvcId = svc.id">Delete</button>',
    '          <template v-else>',
    '            <button type="button" class="btn btn--danger btn--sm" @click="doDeleteService(svc.id)">',
    '              Delete (past turns kept)</button>',
    '            <button type="button" class="btn btn--quiet btn--sm" @click="confirmSvcId = null">Cancel</button>',
    '          </template>',
    '        </div>',
    '      </li>',
    '    </ul>',
    '  </template>',
    '',
    /* ---------------- board & day ---------------- */
    '  <template v-else-if="ui.configTab === \'board\'">',
    '    <div class="config__intro">',
    '      <h2>Board &amp; day</h2>',
    '      <p><span>Display options for the board, plus the controls for clearing today and for this station&rsquo;s sign-in. Everything here is saved for the <strong>whole salon</strong> and reaches the other stations on its own &mdash; except the column lock and the folded-away rows, which belong to the screen you are standing at.</span></p>',
    '    </div>',
    '',
    /* The cards flow into as many columns as the window has room for — see
       `.config__cols`. A card marked `--wide` spans the lot, for the two or
       three whose controls are a row rather than a stack. */
    '    <div class="config__cols">',
    /* First on the tab, because two of the cards under it change behaviour
       once these are filled in. Wide: seven days of open-and-shut times are a
       row apiece, and a row that wraps is seven rows that wrap. */
    '    <div class="add-card add-card--wide">',
    '      <h3>Business hours</h3>',
    '      <div class="card-split">',
    '      <div class="card-split__prose">',
    '      <p class="setting__hint">When the doors open and shut, one pair of times per day. Two things read them. The board <strong>sets itself up {{ openLead }} minutes before opening</strong> &mdash; the crew {{ schedulingName }} has on today is marked in and the picker opens for whoever is left &mdash; instead of doing it the moment the day&rsquo;s book arrives, which on a station left running overnight is a minute past midnight. And Next availability stops offering work the day is not long enough for: see below.</p>',
    '      <p class="setting__hint">Leave a day <strong>closed</strong> and the board does not set itself up on it at all. Nothing is set out of the box, and with nothing set neither of those happens &mdash; the morning behaves exactly as it always has, and no service is ever held back.</p>',
    '      </div>',
    '      <div class="card-split__main">',
    '      <ul class="hours-list">',
    '        <li v-for="row in hoursRows" :key="row.dow" class="hours-row"',
    '          :class="{ \'is-today\': row.dow === todayDow, \'is-closed\': row.closed }">',
    '          <label class="switch hours-row__day">',
    '            <input type="checkbox" :checked="!row.closed"',
    '              @change="setDayHours(row.dow, { closed: !$event.target.checked })" />',
    '            <span class="switch__track"><span class="switch__thumb"></span></span>',
    '            <span class="switch__label">{{ row.name }}</span>',
    '          </label>',
    '          <template v-if="!row.closed">',
    '            <input class="input input--clock" type="time" :value="row.open"',
    '              :aria-label="row.name + \' opening time\'"',
    '              @change="setDayHours(row.dow, { open: $event.target.value })" />',
    '            <span class="hours-row__dash">&ndash;</span>',
    '            <input class="input input--clock" type="time" :value="row.close"',
    '              :aria-label="row.name + \' closing time\'"',
    '              @change="setDayHours(row.dow, { close: $event.target.value })" />',
    '            <button type="button" class="link-btn hours-row__copy"',
    '              title="Put these times on every other open day"',
    '              @click="copyHours(row.dow)">Copy to all</button>',
    '          </template>',
    '          <span v-else class="hours-row__shut">Closed</span>',
    '          <span v-if="row.dow === todayDow" class="hours-row__tag">Today</span>',
    '        </li>',
    '      </ul>',
    '      </div>',
    '      </div>',
    '      <p v-if="hoursSet && todayHours" class="setting__hint">Today: <strong>{{ hoursLabel(hoursRows[todayDow]) }}</strong>. The board sets itself up at <strong>{{ formatClock(todayHours.startAt) }}</strong>, and Next availability offers nothing that would finish after <strong>{{ formatClock(todayHours.closeAt + closingGrace * 60000) }}</strong>.</p>',
    '      <p v-else-if="hoursSet" class="warn-line">Today is marked closed, so the board will not set itself up and Next availability is not holding anything back. Mark technicians in from the <strong>+</strong> on the board if the salon is open after all.</p>',
    '      <div class="add-card__row">',
    '        <button v-if="hoursSet && !clearHours" type="button" class="btn btn--danger-ghost btn--sm"',
    '          @click="clearHours = true">Clear business hours</button>',
    '        <template v-else-if="hoursSet">',
    '          <button type="button" class="btn btn--danger btn--sm" @click="doClearHours">',
    '            Clear all seven days</button>',
    '          <button type="button" class="btn btn--quiet btn--sm" @click="clearHours = false">Cancel</button>',
    '        </template>',
    '      </div>',
    '    </div>',
    '',
    '    <div class="add-card">',
    '      <h3>Closing time</h3>',
    '      <p v-if="!hoursSet" class="setting__hint">Nothing here is in force: it needs the <strong>business hours</strong> above.</p>',
    '      <p class="setting__hint">A walk-in is not free &mdash; it runs for as long as the work takes. With hours set, Next availability measures every service against the closing time from the soonest it could actually start, and marks the ones that would not finish in the day.</p>',
    '      <p class="setting__hint"><span class="setting__moon">&#9790;</span> on a card means that service would <strong>run past closing</strong>. It is still offered: how late is a question for whoever it lands on, and the tooltip on the moon says the finishing time so the desk can go and ask. Once it would run <strong>more than {{ closingGrace }} minutes over</strong>, the card greys out and pressing it explains why instead of arming it &mdash; the same for the hotkey.</p>',
    '      <p class="setting__hint">It only ever holds a service back from <strong>Next availability</strong>. Clicking a technician&rsquo;s empty square still starts anything for anybody, which is where work a technician has agreed to stay for goes up. Bookings are never touched: a service already in the book is somebody&rsquo;s appointment, not an offer. A turn type with no usual length under <strong>Settings &rarr; Services</strong> cannot be measured and is never held back.</p>',
    /* The other thing the closing time buys, and the only one that is a plain
       reading rather than a refusal. Documented beside the refusals because it
       comes from the same setting and is the first thing somebody wonders about
       when a fourth entry appears in a bar that has had three all day. */
    '      <p class="setting__hint">For the last <strong>{{ lastFinishWindow }} hours</strong> of the day the top bar also carries <strong>done ~time</strong>: when the last thing still on the board &mdash; every running square, and everything left in the book &mdash; is expected to finish. It turns red once that lands after closing. Anything with no expected length is left out and the tooltip says how many, so the number is never quietly short.</p>',
    '    </div>',
    '',
    '    <div class="add-card">',
    '      <h3>Board layout</h3>',
    '      <p class="setting__hint">Which way round the grid is drawn. Out of the box each technician is a <strong>column</strong> and each turn a row, counting down the screen. Turned round, each technician is a <strong>row</strong> and each turn a column, counting across &mdash; which suits a wide screen with a short roster, and reads a technician&rsquo;s day left to right the way a diary does.</p>',
    '      <div class="add-card__row add-card__row--fields">',
    '        <div class="layout-pick" role="radiogroup" aria-label="Which way round the board is drawn">',
    '          <button v-for="opt in boardLayouts" :key="opt.id" type="button" class="layout-pick__opt"',
    '            :class="{ \'is-on\': ui.boardLayout === opt.id }" role="radio"',
    '            :aria-checked="ui.boardLayout === opt.id ? \'true\' : \'false\'"',
    '            @click="setBoardLayout(opt.id)">',
    /* The grid drawn small, because this is a question about a shape and a
       shape is the one thing a sentence is bad at. Navy blocks are the staff
       headers, so the two pictures differ exactly where the boards do. */
    '            <span class="layout-pick__mini" aria-hidden="true">',
    '              <span v-for="n in 12" :key="n" class="layout-pick__sq"',
    '                :class="{ \'is-head\': opt.id === \'columns\' ? n <= 4 : n % 4 === 1 }"></span>',
    '            </span>',
    '            <span class="layout-pick__name">{{ opt.name }}</span>',
    '            <span class="layout-pick__note">{{ opt.note }}</span>',
    '          </button>',
    '        </div>',
    '      </div>',
    '      <p class="setting__hint">Nothing about the day changes with it: the same turns, in the same order, with the same rotation deciding who is up. Dragging a name still reorders the roster and dragging a square still moves a service &mdash; along whichever axis they are on. Saved for the whole salon, so the wall screen and the desk agree on which way round the board is.</p>',
    '      <p class="setting__hint">A phone still gets the <strong>list</strong> either way: four inches has room for one axis, and which one the salon prefers on a monitor does not change that.</p>',
    '    </div>',
    '',
    '    <div class="add-card">',
    '      <h3>Running squares</h3>',
    '      <p class="setting__hint">What a square does while the service on it is running. The strip along the bottom of it &mdash; the one carrying the countdown &mdash; is the same strip either way: same place, same wording, and the same amber in the last five minutes and red once it is over. Only what happens <em>inside</em> it changes.</p>',
    '      <div class="add-card__row add-card__row--fields">',
    '        <div class="layout-pick" role="radiogroup" aria-label="How a running square shows it is running">',
    '          <button v-for="opt in turnProgressModes" :key="opt.id" type="button" class="layout-pick__opt"',
    '            :class="{ \'is-on\': ui.turnProgress === opt.id }" role="radio"',
    '            :aria-checked="ui.turnProgress === opt.id ? \'true\' : \'false\'"',
    '            @click="setTurnProgress(opt.id)">',
    /* The square itself, drawn small and actually running — the light blinks
       and the bar sits part-filled. A picture of the thing rather than a word
       for it: this is a question about what the board looks like across the
       room, which is not something a sentence answers well. */
    '            <span class="prog-pick__mini" aria-hidden="true">',
    '              <span class="prog-pick__letter">P</span>',
    '              <span class="prog-pick__strip">',
    '                <span v-if="opt.id === \'bar\'" class="prog-pick__fill"></span>',
    '                <span v-else class="prog-pick__dot"></span>',
    '              </span>',
    '            </span>',
    '            <span class="layout-pick__name">{{ opt.name }}</span>',
    '            <span class="layout-pick__note">{{ opt.note }}</span>',
    '          </button>',
    '        </div>',
    '      </div>',
    '      <p class="setting__hint">The <strong>light</strong> is the board out of the box: a gold light blinking beside the countdown for as long as the service is live. It says a square is running, and nothing more &mdash; which is all a square can honestly say when nobody knows how long the work takes.</p>',
    '      <p class="setting__hint">The <strong>bar</strong> fills that same strip left to right as the booked length runs down, so a glance across the board reads how far through everybody is rather than only who is busy. It needs a length to measure against: an attached booking, or the usual length of the turn type under <strong>Settings &rarr; Services</strong>. A turn with <strong>neither</strong> keeps the blinking light, because there is nothing for the time so far to be a fraction of &mdash; so a floor of walk-ins on unmeasured services looks exactly as it does today.</p>',
    '      <p class="setting__hint">The bar stops when it is full. Past the booked time the strip is already red and reads how far over, and a bar that carried on would have to mean two things at once. Finished squares are untouched either way &mdash; they have stopped counting down and read the length the service took.</p>',
    '      <p class="setting__hint">A <strong>half square</strong> with one of its two services still running borrows the whole strip for it, filling as that service runs down, and the half that is not running goes <strong>grey</strong> &mdash; one bar across a square holding two services cannot otherwise say which of them it is counting. The grey half stays perfectly readable, and keeps its own small countdown: that is the record of how long that service took. On the blinking light, half squares are drawn exactly as they are today.</p>',
    '      <p class="setting__hint">Saved for the whole salon, and phones show neither &mdash; a square four inches wide gives its room to the client and the service.</p>',
    '    </div>',
    '',
    '    <div class="add-card">',
    '      <h3>Display</h3>',
    '      <label class="switch">',
    '        <input type="checkbox" :checked="ui.showNextUp"',
    '          @change="setShowNextUp($event.target.checked)" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">Show who is up next on each service</span>',
    '      </label>',
    '      <p class="setting__hint">Adds the <strong>Next</strong> column to every row in Next availability, naming the technician that service would go to. With everybody who does it mid-service, it names whoever will have room for it first &mdash; a countdown up to half an hour (<strong>~25m</strong>), a time to come back at beyond that (<strong>3:45 PM</strong>). Reads <strong>None</strong>, greyed out, when nobody in today performs it at all.</p>',
    '    </div>',
    '',
    /* Self check-in used to sit here, and moved to its own tab once it grew
       colours, wording and a service list of its own — everything about the
       lobby screen is now in one place rather than split across two tabs by
       whether it happened to be a switch. */
    /* Only with a book to read: with no scheduling plugin there is no Due now
       row for this to be the width of. */
    '    <div v-if="scheduled" class="add-card">',
    '      <h3>Due now</h3>',
    '      <p class="setting__hint">How far ahead the <strong>Due now</strong> row beside <strong>Next availability</strong> looks. A booking joins it this many minutes before its start time. Ten out of the box; a desk that seats people as they arrive wants less, a desk that takes the paperwork first wants more.</p>',
    '      <div class="add-card__row add-card__row--fields">',
    '        <div class="weight-pick" role="radiogroup" aria-label="How early a booking joins Due now">',
    '          <button v-for="m in dueSoonChoices" :key="m" type="button" class="weight-pick__opt"',
    '            :class="{ \'is-on\': !dueIsCustom && dueSoonMinutes === m }" role="radio"',
    '            :aria-checked="!dueIsCustom && dueSoonMinutes === m ? \'true\' : \'false\'"',
    '            @click="pickDueSoon(m)">{{ m }} min</button>',
    '          <button type="button" class="weight-pick__opt" role="radio"',
    '            :class="{ \'is-on\': dueIsCustom }"',
    '            :aria-checked="dueIsCustom ? \'true\' : \'false\'"',
    '            @click="dueCustom = true">Custom</button>',
    '        </div>',
    '        <label v-if="dueIsCustom" class="field">',
    '          <span class="field__label">Minutes</span>',
    '          <input class="input input--mins" type="number" :value="dueSoonMinutes"',
    '            :min="dueSoonLimits.min" :max="dueSoonLimits.max" step="1"',
    '            aria-label="Minutes before a booking joins Due now"',
    '            @change="onDueSoonInput($event.target.value)" />',
    '        </label>',
    '      </div>',
    '      <p class="setting__hint">Nothing ever drops off the row <em>early</em>: a booking whose time has been and gone stays on it, reading <strong>Due</strong> instead of a clock time, until it would have finished. A client standing at the desk ten minutes late is the most urgent thing on the bar, not the least &mdash; that end of it is not a setting.</p>',
    '      <p class="setting__hint">Saved for the whole salon, so every station agrees on what is due.</p>',
    '    </div>',
    '',
    '    <div v-if="scheduled" class="add-card">',
    '      <h3>Conflict buffer</h3>',
    '      <p class="setting__hint">How far behind a technician can be before <strong>Potential conflicts</strong> lists a booking as <strong>coming up without a tech ready for it</strong>. With five, somebody finishing at 2:04 for a 2:00 client is fine and somebody finishing at 2:06 is listed. The same allowance goes for a booking of theirs running into the next one. Five out of the box; <strong>0</strong> lists a single minute over.</p>',
    '      <div class="add-card__row add-card__row--fields">',
    '        <div class="weight-pick" role="radiogroup" aria-label="How far behind before it is a conflict">',
    '          <button v-for="m in conflictBufferChoices" :key="m" type="button" class="weight-pick__opt"',
    '            :class="{ \'is-on\': !bufferIsCustom && conflictBufferMinutes === m }" role="radio"',
    '            :aria-checked="!bufferIsCustom && conflictBufferMinutes === m ? \'true\' : \'false\'"',
    '            @click="pickBuffer(m)">{{ m }} min</button>',
    '          <button type="button" class="weight-pick__opt" role="radio"',
    '            :class="{ \'is-on\': bufferIsCustom }"',
    '            :aria-checked="bufferIsCustom ? \'true\' : \'false\'"',
    '            @click="bufferCustom = true">Custom</button>',
    '        </div>',
    '        <label v-if="bufferIsCustom" class="field">',
    '          <span class="field__label">Minutes</span>',
    '          <input class="input input--mins" type="number" :value="conflictBufferMinutes"',
    '            :min="conflictBufferLimits.min" :max="conflictBufferLimits.max" step="1"',
    '            aria-label="Minutes behind before a booking is a conflict"',
    '            @change="onBufferInput($event.target.value)" />',
    '        </label>',
    '      </div>',
    '      <p class="setting__hint">It never hides two clients booked at the same minute, and it has nothing to do with somebody whose hours end before a booking would be over &mdash; those are listed whatever this says.</p>',
    '    </div>',
    '',
    '    <div class="add-card">',
    '      <h3>Client requests</h3>',
    '      <label class="switch">',
    '        <input type="checkbox" :checked="ui.requestsOn"',
    '          @change="setRequestsOn($event.target.checked)" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">Track client requests</span>',
    '      </label>',
    '      <p class="setting__hint">Off by default. <strong>{{ schedulingName }} does not say whether a client asked for a technician</strong> &mdash; every booked service carries exactly one assigned tech whether the client asked for them or the desk simply put them there &mdash; so the board can never work it out on its own. It has to be <strong>told</strong>, one booking at a time.</p>',
    '      <p class="setting__hint">With it on: <strong>right-click a service</strong> in today&rsquo;s book or a Due now chip to say the client asked for that technician. The booking is marked with a <span class="setting__star">&#9733;</span>, the square carries it when the turn starts, the column header marks it beside the <strong>next</strong> clock, and nothing else is offered around that technician while it runs. The <strong>Request</strong> button on the top bar and the boxes on the start-a-turn sheets do the same for a walk-in, and a square can be starred from its own menu. Requests are counted in the day summary and in History.</p>',
    '      <p class="setting__hint">Turning it off <strong>hides</strong> requests; it never erases them. Stars already recorded stay on their turns and come back if this is switched on again. Either way the board still knows a technician is <strong>booked</strong> at a time and will not offer them around &mdash; that part is what {{ schedulingName }} actually says, not a guess about who asked.</p>',
    '      <p class="setting__hint">Saved for the whole salon, so every station agrees. It takes effect on the others within a second or two, and on a station with no internet as soon as it is back.</p>',
    '    </div>',
    '',
    /* Only when something can report a checkout — otherwise this is a switch
       with nothing behind it. */
    '    <div v-if="canCheckout" class="add-card">',
    '      <h3>Checkout</h3>',
    '      <label class="switch">',
    '        <input type="checkbox" :checked="ui.autoStop"',
    '          @change="setAutoStop($event.target.checked)" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">Stop a turn when {{ schedulingName }} closes its ticket</span>',
    '      </label>',
    '      <p class="setting__hint">When an appointment is checked out, any turn still running against it stops <strong>as of the checkout time</strong> &mdash; not the moment the board noticed. Turn this off to stop every turn by hand. It only ever stops turns; nothing is written back to {{ schedulingName }}.</p>',
    '    </div>',
    '',
    /* Only with a book to start from. */
    '    <div v-if="scheduled" class="add-card">',
    '      <h3>In service</h3>',
    '      <label class="switch">',
    '        <input type="checkbox" :checked="ui.autoStart"',
    '          @change="setAutoStart($event.target.checked)" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">Start the rest of a visit when a client is seated</span>',
    '      </label>',
    '      <p class="setting__hint">Putting one of a client&rsquo;s services on the board marks that appointment in service, and every other service on it that is <strong>starting now</strong> gets a turn too, on whoever {{ schedulingName }} has against it. Anything booked for later in the visit waits until its start time moves. Nothing starts just because a booking&rsquo;s time has come round &mdash; a client has to be seated first.</p>',
    '    </div>',
    '',
    '    <div class="add-card">',
    '      <h3>Today</h3>',
    '      <p class="setting__hint">Deletes every turn recorded for {{ state.businessDate }} and resets missed turns. Past days are not touched.</p>',
    '      <div class="add-card__row">',
    '        <button v-if="!confirmReset" type="button" class="btn btn--danger-ghost"',
    '          @click="confirmReset = true">Reset today&rsquo;s board</button>',
    '        <template v-else>',
    '          <button type="button" class="btn btn--danger" @click="doReset">',
    '            Delete {{ state.turns.length }} turns</button>',
    '          <button type="button" class="btn btn--quiet" @click="confirmReset = false">Cancel</button>',
    '        </template>',
    '      </div>',
    '    </div>',
    '',
    '    <div class="add-card">',
    '      <h3>Station</h3>',
    '      <p v-if="auth.user" class="setting__hint">Signed in as <strong>{{ auth.user.email }}</strong>. Every board table needs this session &mdash; signed out, nothing can be read or saved.</p>',
    '      <p v-else class="setting__hint">Not signed in. Every board table needs a station session, so nothing can be read or saved until this station signs in.</p>',
    '      <div v-if="auth.user" class="add-card__row">',
    '        <button type="button" class="btn btn--ghost" @click="signOut">Sign out</button>',
    '      </div>',
    /* Inline rather than a jump to the pane: with the scheduling plugin off
       there is no pane, and this is the only way back in. */
    '      <station-sign-in v-else />',
    '    </div>',
    '    </div>',
    '  </template>',
    '',
    /* ---------------- self check-in ----------------
       Everything about the lobby screen in one place: whether the business is
       sold it, which services go on it, whether it quotes a wait, what colour
       it is and what it says. It was split across Board & day and Branding for
       about an hour, and the argument against that is simply that nobody
       setting up a tablet thinks of "the wording on it" and "the colours on it"
       as two errands. */
    '  <template v-else-if="ui.configTab === \'checkin\'">',
    '    <div class="config__intro">',
    '      <h2>Self check-in</h2>',
    '      <p><span>The screen clients use to check themselves in, on a tablet in the waiting area. They pick what they came for and the board finds the technician &mdash; the same path as pressing that service under <strong>Next availability</strong>, so a square from the lobby is a square like any other. With nobody free they leave a name instead and appear under <strong>Waiting</strong> beside Next availability, in the order they arrived.</span></p>',
    '    </div>',
    '',
    /* The gate, and it is the whole tab when it is closed. Everything below is
       hidden rather than disabled: a screen full of colour pickers that save
       settings for a screen the salon cannot open is worse than an honest
       card, and the honest card is also the one that gets somebody to ring. */
    '    <div v-if="!checkinLicensed" class="add-card add-card--locked">',
    '      <h3>Not switched on for this business</h3>',
    '      <p v-if="licence.lapsed" class="setting__hint">The subscription for this business has <strong>ended</strong>. Nothing has been deleted &mdash; the check-in screen, its wording and anyone still on the waiting list all come back exactly as they were when it is renewed.</p>',
    '      <p v-else class="setting__hint">Self check-in is not part of {{ licence.planName ? "the " + licence.planName + " plan" : "this subscription" }}. Nothing here is broken or missing; it has simply not been switched on for this business.</p>',
    '      <p class="setting__hint">To use it, <strong>upgrade the plan</strong> &mdash; speak to whoever manages this console, or to sales where you subscribed. It takes effect within a few minutes of being granted, on every station and on the tablet, with no reinstall and nothing to set up again.</p>',
    '      <p class="setting__hint">Until then the console is entirely unaffected: the board, the book and the front desk all carry on exactly as they do now.</p>',
    '    </div>',
    '',
    '    <template v-else>',
    '    <div class="config__cols">',
    /* First, because it is the question somebody opening this tab has. */
    '    <div class="add-card">',
    '      <h3>Putting a tablet out</h3>',
    '      <p class="setting__hint">Open <strong>{{ checkinUrl }}</strong> on the tablet and sign it in once with a station account &mdash; it stays signed in. Add it to the tablet&rsquo;s home screen and it opens without any browser chrome around it.</p>',
    '      <p class="setting__hint">There is deliberately no way to sign out or reach the console from that screen: the person standing in front of it is not staff. Nothing else needs switching on &mdash; a salon with no tablet out simply never sees a waiting list.</p>',
    '    </div>',
    '',
    /* ---- which services ---- */
    /* Wide, and beside its prose: the list is the one card here whose height is
       the salon's rather than ours — five services or thirty — so it is given
       the width to lay them out in columns instead of running off the bottom
       of the tab. See `checkinSvcCols`. */
    '    <div class="add-card add-card--wide">',
    '      <h3>Services on the check-in screen</h3>',
    '      <p class="setting__hint">Its own list, separate from <strong>Next availability</strong> on the board &mdash; a service can be on the desk&rsquo;s bar and off the tablet, or the other way round. Worth keeping the long or complicated ones off it: a ninety-minute set is usually a conversation at the desk rather than a tap by the door.</p>',
    /* The columns get the whole card rather than the half a `card-split` would
       leave them. Deliberately: two columns to a band means a long list wraps
       into three bands, and then reading down the left gives services 1-5,
       11-15, 21-23 — a list you have to work out the order of. The full width
       holds six columns, so thirty services are still one band read straight
       across, which is the only arrangement that needs no working out. */
    '      <div class="checkin-svcs">',
    '        <ul v-for="(col, i) in checkinSvcCols" :key="i" class="checkin-svcs__col">',
    '          <li v-for="svc in col" :key="svc.id" class="checkin-svcs__row">',
    '            <label class="switch switch--sm">',
    '              <input type="checkbox" :checked="svc.selfCheckin !== false"',
    '                @change="updateService(svc.id, { selfCheckin: $event.target.checked })" />',
    '              <span class="switch__track"><span class="switch__thumb"></span></span>',
    '              <span class="switch__label">',
    '                <span class="svc-swatch svc-swatch--sm" :style="{ background: svc.color }">{{ svc.abbrev }}</span>',
    '                {{ svc.name }}</span>',
    '            </label>',
    '          </li>',
    '        </ul>',
    '      </div>',
    '      <p v-if="!checkinServices.length" class="warn-line">Nothing is on the check-in screen, so a client tapping it is sent to the front desk. Switch at least one service on.</p>',
    '      <p v-else class="setting__hint">{{ checkinServices.length }} of {{ services.length }} services are offered.</p>',
    '    </div>',
    '',
    /* ---- wait times ---- */
    '    <div class="add-card">',
    '      <h3>Wait times</h3>',
    '      <label class="switch">',
    '        <input type="checkbox" :checked="ui.checkinWaits"',
    '          @change="setCheckinWaits($event.target.checked)" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">Show wait times on the check-in screen</span>',
    '      </label>',
    '      <p class="setting__hint">On, a service nobody is free for reads <strong>about 20 minutes</strong> &mdash; or <strong>from about 3:45 PM</strong> once the wait is past half an hour, because nobody counts out forty minutes in their head. It is the same estimate the board&rsquo;s <strong>Next</strong> column is, measured from the soonest anyone would have room for the work rather than from the next person to stand up.</p>',
    '      <p class="setting__hint">Off, the same service reads <strong>Waiting list</strong>, and the screen says everyone is with a client without saying for how long. Nothing else changes: people still join, and are still told their place.</p>',
    '      <p class="setting__hint">Worth turning off in a salon where the day does not run to time. The estimate assumes it does, and a client who was told twenty minutes is owed twenty minutes &mdash; a technician reads <strong>~25m</strong> as a working guess and knows what it is worth, where a client reads it as a promise the salon made. That is the whole of the difference between this and <strong>Show who is up next</strong> under Board &amp; day, which is why they are two switches.</p>',
    '    </div>',
    '',
    /* ---- booking instead of waiting ----
       Wide, and beside its prose: it is the one card on this tab that changes
       what the screen OFFERS rather than how it looks, so it is worth the room to
       say why — and the per-service ids under it are a list as long as the
       salon's own, like the services card above. */
    '    <div class="add-card add-card--wide">',
    '      <h3>Booking instead of waiting</h3>',
    '      <div class="card-split">',
    '      <div class="card-split__prose">',
    '      <p class="setting__hint">Past a certain wait, a waiting list is not much of a kindness. This is where a client is sent instead: the screen stops offering a place in the queue and offers a <strong>booking link</strong>, on a day the salon can actually take them.</p>',
    '      <p class="setting__hint">It replaces the waiting list on that service rather than sitting beside it &mdash; one screen, one thing to do. The name box goes with it, because nothing is written down: nobody appears under <strong>Waiting</strong> at the desk, so a client who books is a client the salon hears about from its booking system like any other.</p>',
    '      <p class="setting__hint"><strong>Nothing happens at all until the link is set.</strong> A screen that says to book and gives no way to do it is worse than the queue it replaced &mdash; somebody who would have left their name leaves the salon instead, and the desk never learns they came. So with the box below empty, a long wait still offers the waiting list however the cutoff is set.</p>',
    '      <p class="setting__hint">The link opens in a <strong>new tab</strong>, so the check-in screen is still underneath it for the next person &mdash; a tablet on a wall bracket has nobody who can reach the address bar.</p>',
    '      </div>',
    '      <div class="card-split__main">',
    '      <div class="ci-text">',
    '        <span class="ci-text__label">Booking link</span>',
    /* type="url" for the tablet keyboard the desk might be on, and `change`
       rather than `input` so a write does not go up per keystroke. The box reads
       back what was STORED, which is the cleaned link — a paste with no https on
       the front comes back with one, and something that is not a web address at
       all comes back empty, which is how a typo announces itself here. */
    '        <input class="input" type="url" spellcheck="false"',
    '          :value="bookingUrl" placeholder="https://booking.example.com/?serviceId={service}"',
    '          @change="setBookingUrl($event.target.value)"',
    '          @keyup.enter="$event.target.blur()" />',
    '        <span class="ci-field__hint">Paste the salon&rsquo;s own booking page. Put <code class="tok">{{ bookPlaceholder }}</code> where the booking system expects a service, and each service below fills it in &mdash; so somebody who tapped Pedicure lands on Pedicure. Leave it out and every service goes to the same page, which is a perfectly good way to set this up.</span>',
    '      </div>',
    /* The example is the whole of the feedback on a link somebody is typing: a
       template with a placeholder in it is not something you can check by
       reading it. Resolved through the same call the tablet makes. */
    '      <p v-if="bookExample" class="setting__hint">A client tapping <strong>{{ bookExample.name }}</strong> is sent to <code class="tok">{{ bookExample.url }}</code></p>',
    '',
    '      <label class="switch">',
    '        <input type="checkbox" :checked="!!bookAfter"',
    '          @change="setBookCutoff($event.target.checked)" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">Send them to book once the wait is long</span>',
    '      </label>',
    '      <div v-if="bookAfter" class="add-card__row add-card__row--fields">',
    '        <label class="field">',
    '          <span class="field__label">Longer than, in minutes</span>',
    '          <input class="input input--mins" type="number" :min="bookLimits.min"',
    '            :max="bookLimits.max" step="5" :value="bookAfter"',
    '            aria-label="Minutes of wait before a client is sent to book"',
    '            @input="onBookCutoffInput($event.target.value)" />',
    '        </label>',
    '      </div>',
    '      <p v-if="bookAfter && !bookingUrl" class="warn-line">No booking link is set, so this does nothing yet &mdash; a long wait still offers the waiting list.</p>',
    '      <p v-if="!bookAfter" class="setting__hint">Off, so <strong>everybody is offered the waiting list</strong> however long the wait is. The link is still what the screen would use if this is switched back on.</p>',
    '      <p v-else class="setting__hint">The estimate is the same one the board&rsquo;s <strong>Next</strong> column shows, and it is read whether or not <strong>Wait times</strong> above is switched on &mdash; that switch decides whether a client is told a number, not how long the wait is.</p>',
    '      <p class="setting__hint">Somebody who is free right now is never sent away, whatever the estimate says for the rest of the floor &mdash; and neither is the <strong>closing</strong> case, which still goes to the front desk. A person is a better answer than a link when there is one available.</p>',
    '      </div>',
    '      </div>',
    '',
    /* ---- the per-service ids ----
       Only with a placeholder in the link, because without one there is nothing
       for an id to fill and a column of empty boxes is a page of homework
       somebody has been set for no reason. */
    '      <template v-if="bookingPerService">',
    '      <h4 class="ci-text-group__name">{{ scheduled ? schedulingName + " service ids" : "Service ids" }}</h4>',
    '      <p class="setting__hint">What <code class="tok">{{ bookPlaceholder }}</code> is replaced with, one per service on the tablet. It is the booking system&rsquo;s own id for that service &mdash; copy it out of {{ scheduled ? schedulingName : "the booking system" }}, usually from the address bar of its own booking page for that service.</p>',
    '      <p v-if="!scheduled" class="setting__hint">No booking system is connected under <strong>Appointments</strong>, which is fine: these are typed in either way, and a salon with a booking page and no integration uses exactly this.</p>',
    '      <div class="book-ids">',
    '        <label v-for="svc in checkinServices" :key="svc.id" class="book-ids__row">',
    '          <span class="book-ids__name">',
    '            <span class="svc-swatch svc-swatch--sm" :style="{ background: svc.color }">{{ svc.abbrev }}</span>',
    '            {{ svc.name }}</span>',
    '          <input class="input input--ref" type="text" :maxlength="bookRefMax"',
    '            spellcheck="false" :value="svc.bookingServiceId"',
    '            placeholder="no id" :aria-label="svc.name + \' booking id\'"',
    '            @change="updateService(svc.id, { bookingServiceId: $event.target.value })"',
    '            @keyup.enter="$event.target.blur()" />',
    '        </label>',
    '      </div>',
    '      <p v-if="!checkinServices.length" class="setting__hint">Nothing is on the check-in screen yet, so there is nothing to give an id to.</p>',
    '      <p v-else-if="bookIdCount < checkinServices.length" class="setting__hint">{{ bookIdCount }} of {{ checkinServices.length }} have one. The rest still work &mdash; they land on the booking site&rsquo;s own page instead of on that service.</p>',
    '      <p v-else class="setting__hint">All {{ checkinServices.length }} of them have one.</p>',
    '      </template>',
    '    </div>',
    '',
    /* ---- colours ----
       Wide: six colour wells side by side are one glance at the scheme, where
       six stacked in a narrow column are a list you have to remember down. */
    '    <div class="add-card add-card--wide">',
    '      <h3>Colours</h3>',
    '      <div class="card-split">',
    '      <div class="card-split__prose">',
    '      <p class="setting__hint">The check-in screen is the only part of this console a <strong>client</strong> ever sees, and it sits in the waiting area next to the salon&rsquo;s own signage &mdash; so it gets its own colours rather than the board&rsquo;s. Every one of them starts as whatever <strong>Branding</strong> is set to, so a screen nobody has touched is already in the right colours; clear a box to hand it back.</p>',
    '      <p class="setting__hint">The <strong>logo</strong> from Branding is shown at the top of the screen, with the business name in its place when there is no logo. There is nothing to set here for that.</p>',
    '      </div>',
    '      <div class="card-split__main">',
    '      <div class="ci-fields">',
    '        <label v-for="f in colorFields" :key="f.key" class="ci-field">',
    '          <span class="ci-field__label">{{ f.label }}</span>',
    '          <span class="ci-field__row">',
    /* A colour well and a text box on the same value. The well is how somebody
       picks one; the box is how somebody pastes the hex their designer sent,
       and is the only one of the two that can be EMPTIED — which is what hands
       the colour back to the theme. A native picker has no empty. */
    '            <input type="color" class="ci-field__well" :value="look.colors[f.key]"',
    '              :aria-label="f.label + \' colour\'"',
    '              @input="setCheckinLook(\'colors\', f.key, $event.target.value)" />',
    '            <input type="text" class="input input--hex" maxlength="7"',
    '              :value="rawLook.colors[f.key] || \'\'" :placeholder="look.colors[f.key]"',
    '              :aria-label="f.label + \' hex\'"',
    '              @change="setCheckinLook(\'colors\', f.key, $event.target.value)" />',
    '          </span>',
    '          <span class="ci-field__hint">{{ f.hint }}</span>',
    '        </label>',
    '      </div>',
    '      </div>',
    '      </div>',
    '      <p class="setting__hint">The text <em>on</em> the buttons is not a setting: it flips between white and near-black on the button colour&rsquo;s own brightness, which is what stops a pale brand colour producing a screen with no visible buttons on it &mdash; on the one screen here with no member of staff standing in front of it to notice.</p>',
    '    </div>',
    '',
    /* ---- wording ----
       The tallest card in the console by some way: sixteen boxes in seven
       groups. Wide, and the groups themselves flow into columns inside it, so
       what was a screen and a half of scrolling is a few rows of panels. Each
       group holds together — see `.ci-text-cols`. */
    '    <div class="add-card add-card--wide">',
    '      <h3>Wording</h3>',
    '      <p class="setting__hint">Every line the check-in screen says, in the order a client meets them. Leave a box empty to use the wording shown in it. Up to {{ textMax }} characters each &mdash; past that a heading wraps into something nobody reads across a waiting room.</p>',
    '      <p class="setting__hint">The one line that is not here is the closing time, which the screen adds by itself when a service would not fit before the doors shut. It is a fact rather than a sentence, so it is not yours to mistype.</p>',
    '      <div class="ci-text-cols">',
    '      <div v-for="g in textGroups" :key="g.name" class="ci-text-group">',
    '        <h4 class="ci-text-group__name">{{ g.name }}</h4>',
    '        <label v-for="f in g.fields" :key="f.key" class="ci-text">',
    '          <span class="ci-text__label">{{ f.label }}</span>',
    '          <input type="text" class="input" :maxlength="textMax"',
    '            :value="rawLook.text[f.key] || \'\'" :placeholder="f.fallback"',
    '            @change="setCheckinLook(\'text\', f.key, $event.target.value)" />',
    '          <span v-if="f.hint" class="ci-field__hint">{{ f.hint }}</span>',
    '        </label>',
    '      </div>',
    '      </div>',
    '    </div>',
    '',
    '    <div v-if="checkinCustomised" class="add-card">',
    '      <h3>Start again</h3>',
    '      <p class="setting__hint">Clears every colour and every line of wording on this tab at once, back to the salon&rsquo;s theme and the shipped sentences. It touches nothing else &mdash; not the services above, not the wait-times switch, and nobody on the waiting list.</p>',
    '      <div class="add-card__row">',
    '        <button v-if="!confirmResetLook" type="button" class="btn btn--danger-ghost btn--sm"',
    '          @click="confirmResetLook = true">Reset colours and wording</button>',
    '        <template v-else>',
    '          <button type="button" class="btn btn--danger btn--sm" @click="doResetLook">Reset them</button>',
    '          <button type="button" class="btn btn--quiet btn--sm" @click="confirmResetLook = false">Cancel</button>',
    '        </template>',
    '      </div>',
    '    </div>',
    '    </div>',
    '    </template>',
    '  </template>',
    '',
    /* ---------------- theme ---------------- */
    '  <template v-else-if="ui.configTab === \'theme\'">',
    '    <div class="config__intro">',
    '      <h2>Branding</h2>',
    '      <p><span>The name and logo in the top bar, and the colours everything is drawn in. All of it is saved <strong>per device</strong>, like the other display settings &mdash; a station by the window can run a different scheme from one in the back.</span></p>',
    '    </div>',
    '',
    '    <div class="add-card">',
    '      <h3>Business name</h3>',
    '      <p class="setting__hint">Shown in the top bar, in the browser tab, and anywhere the console names the business. Nothing is hard-coded &mdash; leave this empty and it reads <strong>{{ brandLimits.fallback }}</strong>.</p>',
    '      <div class="add-card__row">',
    '        <input class="input" type="text" :value="brand.name" :maxlength="brandLimits.maxLen"',
    '          placeholder="The name across the top bar" spellcheck="false"',
    '          @change="setBusinessName($event.target.value)" @keyup.enter="$event.target.blur()" />',
    '      </div>',
    '    </div>',
    '',
    '    <div class="add-card">',
    '      <h3>Logo</h3>',
    '      <p class="setting__hint">Replaces the <strong>{{ businessName }}</strong> wordmark in the top bar. {{ logoLimits.typeLabel }}, up to {{ fileSizeLabel(logoLimits.maxBytes) }}, no larger than {{ logoLimits.maxW }}&times;{{ logoLimits.maxH }} and at least {{ logoLimits.minH }}px tall. It is stored on this device, so each station needs its own copy.</p>',
    '      <div class="logo-row">',
    '        <div class="logo-preview" :class="{ \'is-empty\': !logo.value }">',
    '          <img v-if="logo.value" :src="logo.value.dataUrl" alt="Current logo" />',
    '          <span v-else>No logo</span>',
    '        </div>',
    '        <div class="logo-row__main">',
    '          <label class="btn btn--ghost logo-pick">',
    '            <input type="file" :accept="logoLimits.types.join(\',\')"',
    '              @change="onLogoPick($event)" />',
    '            <span>{{ logo.value ? "Replace logo" : "Choose a file" }}</span>',
    '          </label>',
    '          <button v-if="logo.value" type="button" class="btn btn--danger-ghost btn--sm"',
    '            @click="clearLogo">Remove</button>',
    '          <p v-if="logo.value" class="logo-meta">',
    '            {{ logo.value.width }}&times;{{ logo.value.height }}</p>',
    '        </div>',
    '      </div>',
    '      <p v-if="logo.error" class="warn-line">{{ logo.error }}</p>',
    '    </div>',
    '',
    '    <h3 class="config__sub">Colour scheme</h3>',
    '    <div class="theme-grid">',
    '      <button v-for="p in themePresets" :key="p.id" type="button" class="theme-card"',
    '        :class="{ \'is-on\': theme.preset === p.id }" @click="setThemePreset(p.id)">',
    '        <span class="theme-card__swatches">',
    '          <span :style="{ background: p.colors.primary }"></span>',
    '          <span :style="{ background: p.colors.accent }"></span>',
    '          <span :style="{ background: p.colors.action }"></span>',
    '          <span :style="{ background: p.colors.bg }"></span>',
    '        </span>',
    '        <span class="theme-card__name">{{ p.name }}</span>',
    '        <span class="theme-card__note">{{ p.note }}</span>',
    '      </button>',
    '    </div>',
    '',
    '    <div class="add-card">',
    '      <h3>Custom colours</h3>',
    '      <p class="setting__hint">Each of these overrides the scheme above. Clear a box to hand that colour back to the scheme.</p>',
    '      <ul class="theme-list">',
    '        <li v-for="f in themeFields" :key="f.key" class="theme-row">',
    '          <label class="theme-row__swatch" :style="{ background: themeValue(f.key) }">',
    '            <input type="color" :value="themeValue(f.key)"',
    '              @input="setThemeColor(f.key, $event.target.value)" />',
    '          </label>',
    '          <div class="theme-row__main">',
    '            <span class="theme-row__label">{{ f.label }}<span v-if="theme.custom[f.key]" class="theme-row__tag">custom</span></span>',
    '            <span class="theme-row__hint">{{ f.hint }}</span>',
    '          </div>',
    '          <input type="text" class="input input--hex" spellcheck="false" maxlength="7"',
    '            :value="themeValue(f.key)" :placeholder="themeValue(f.key)"',
    '            @change="setThemeColor(f.key, $event.target.value)" />',
    '        </li>',
    '      </ul>',
    '      <div class="add-card__row">',
    '        <button type="button" class="btn btn--quiet" :disabled="!hasCustomColors"',
    '          @click="clearThemeCustom">Back to the scheme&rsquo;s colours</button>',
    '      </div>',
    '    </div>',
    '  </template>',
    '',
    /* Guarded on `youAreOwner` as well as on the tab, not only because the tab
       is hidden: `ui.configTab` is remembered per device, so a station whose
       owner used it and then demoted them would come back to this panel with no
       tab on screen to leave it by. */
    '  <template v-else-if="ui.configTab === \'people\' && youAreOwner">',
    '    <people-settings />',
    '  </template>',
    '',
    /* ---------------- appointments (the scheduling plugin) ---------------- */
    '  <template v-else>',
    '    <div class="config__intro">',
    '      <h2>Appointments</h2>',
    '      <p><span>Where the board gets the day&rsquo;s book from. Switched off, the turn tracker knows nothing about appointments or anything else still to come &mdash; only whose turn is next.</span></p>',
    '    </div>',
    '',
    '    <div class="add-card">',
    '      <h3>Booking system</h3>',
    '      <p v-if="!schedulingLicensed" class="warn-line">',
    '        Appointments are not part of',
    '        {{ licence.planName ? "the " + licence.planName + " plan" : "this subscription" }}.',
    '        The board runs on its own &mdash; turns, staff and services all work; it simply',
    '        knows nothing about the day&rsquo;s book.</p>',
    '      <p v-else-if="!schedulingPlugins.length" class="warn-line">',
    '        No booking system is installed, so there is nothing to switch on.</p>',
    '      <template v-else>',
    '        <label class="switch">',
    '          <input type="checkbox" :checked="schedulingWanted"',
    '            @change="onSchedulingToggle($event.target.checked, $event.target)" />',
    '          <span class="switch__track"><span class="switch__thumb"></span></span>',
    '          <span class="switch__label">Connect to {{ schedulingName }}</span>',
    '        </label>',
    '        <p class="setting__hint">On, the board gains the appointments pane, the <strong>next</strong> clock on every name, assigning a turn straight from the book, and the service mapping below. Off, all of it goes &mdash; turns already on the board are untouched either way.</p>',
    '',
    '        <ul v-if="schedulingPlugins.length > 1" class="plugin-list">',
    '          <li v-for="p in schedulingPlugins" :key="p.id">',
    '            <button type="button" class="plugin-card"',
    '              :class="{ \'is-on\': schedulingPlugin && schedulingPlugin.id === p.id }"',
    '              @click="setSchedulingPlugin(p.id)">',
    '              <span class="plugin-card__name">{{ p.name }}</span>',
    '              <span class="plugin-card__blurb">{{ p.blurb }}</span>',
    '            </button>',
    '          </li>',
    '        </ul>',
    '        <p v-else-if="schedulingPlugin" class="setting__hint">{{ schedulingPlugin.blurb }}</p>',
    /* No "N mappings are still stored" line here: switched off, the board has
       let go of the map, so the only honest count would mean reading the
       plugin's table while the plugin is off. The switch-off modal says what
       happens to it instead. */
    '      </template>',
    '    </div>',
    '',
    '    <template v-if="scheduled && canCatalogue">',
    '      <h3 class="config__sub">Service mapping</h3>',
    '      <p class="config__lead">Point each {{ schedulingName }} service at the turn type it counts as. The list is grouped by that type &mdash; what is <strong>not mapped yet</strong> is at the top and open, and every other heading is what counts as it. The appointments pane badges every booked service with its type, and anything unmapped shows a <strong>?</strong>.</p>',
    '',
    '      <p v-if="!auth.user" class="config__empty">Sign in under <strong>Board &amp; day</strong> to load the {{ schedulingName }} service list.</p>',
    '      <template v-else>',
    '      <div class="add-card">',
    '        <div class="add-card__row">',
    '          <input v-model="appts.mapFilter" type="search" class="input" placeholder="Search services" />',
    '          <label class="switch">',
    '            <input type="checkbox" v-model="appts.unmappedOnly" />',
    '            <span class="switch__track"><span class="switch__thumb"></span></span>',
    '            <span class="switch__label">Unmapped only</span>',
    '          </label>',
    '        </div>',
    /* How much of the booking system's list has a turn type against it. The
       bar is the only thing on the page that answers "am I nearly done". */
    '        <div class="map-progress" :title="mappedCount + \' of \' + appts.catalogue.length + \' mapped\'">',
    '          <span class="map-progress__bar">',
    '            <span class="map-progress__fill"',
    '              :style="{ width: (appts.catalogue.length ? (mappedCount / appts.catalogue.length * 100) : 0) + \'%\' }"></span>',
    '          </span>',
    '          <span class="map-progress__n">{{ mappedCount }} of {{ appts.catalogue.length }} mapped</span>',
    '        </div>',
    '      </div>',
    '',
    '      <p v-if="!appts.catalogueLoaded" class="config__empty">Loading services…</p>',
    '      <p v-else-if="!filteredCatalogue.length" class="config__empty">No services match.</p>',
    '',
    /* One section per turn type, holding what counts as it, with everything
       still unmapped at the top and open. */
    '      <div v-for="g in mapGroups" :key="g.key" class="map-group"',
    '        :class="{ \'is-lead\': g.lead, \'is-open\': groupOpen(g.key) }">',
    '        <button type="button" class="map-group__head" @click="toggleGroup(g.key)"',
    '          :aria-expanded="groupOpen(g.key) ? \'true\' : \'false\'">',
    /* Drawn in CSS rather than set as a glyph: at this size a triangle from
       the font is a smudge, and it turns instead of being swapped. */
    '          <span class="map-group__caret" aria-hidden="true"></span>',
    '          <span class="map-group__letter" :style="{ background: g.color }">{{ g.letter }}</span>',
    '          <span class="map-group__name">{{ g.name }}</span>',
    '          <span class="map-group__n">{{ g.items.length }}</span>',
    '        </button>',
    '        <ul v-if="groupOpen(g.key)" class="map-list">',
    '          <li v-if="!g.items.length" class="map-row map-row--empty">Nothing counts as this yet.</li>',
    '          <li v-for="m in g.items" :key="m.id" class="map-row">',
    '            <span class="map-row__name">{{ m.name }}</span>',
    /* One control per row instead of a chip per turn type. A hundred and fifty
       services against eleven types is two thousand buttons as chips; as
       selects it is a hundred and fifty rows you can read. */
    '            <select class="input map-row__select"',
    '              :class="{ \'is-none\': !appts.serviceMap[m.id] }"',
    '              :value="appts.serviceMap[m.id] || \'\'"',
    '              @change="mapExternalService(m.id, $event.target.value || null)">',
    '              <option value="">Not mapped</option>',
    '              <option v-for="svc in services" :key="svc.id" :value="svc.id">{{ svc.name }}</option>',
    '            </select>',
    '          </li>',
    '        </ul>',
    '      </div>',
    '      </template>',
    '    </template>',
    '',
    /* ---- what a booking's options do to the turn it becomes ---- */
    '    <template v-if="scheduled && canOptions && auth.user">',
    '      <h3 class="config__sub">Service options</h3>',
    '      <p class="config__lead">A booking can carry options that change what the work actually is. Give an option a <strong>turn type</strong> and any service booked with it counts as that type instead &mdash; a Classic Pedicure with Gel on it is a Gel Pedicure, and takes that type&rsquo;s turn weight. Name <strong>technicians</strong> and only they can be given it, on the board and in the book. Leave both blank and the option is just a note on the booking.</p>',
    '',
    '      <div class="add-card">',
    '        <div class="add-card__row">',
    '          <input v-model="appts.optionFilter" type="search" class="input" placeholder="Search options" />',
    '          <span class="staff-row__stat">{{ optionRuleCount }} option{{ optionRuleCount === 1 ? "" : "s" }} with a rule</span>',
    '        </div>',
    '      </div>',
    '',
    '      <p v-if="!appts.optionCatalogueLoaded" class="config__empty">Loading options…</p>',
    '      <p v-else-if="!filteredOptions.length" class="config__empty">No options match.</p>',
    '',
    /* Closed, an option is a line saying what it does — which is what the page
       is read for. The two questions only appear for the one being changed,
       so the page is a list rather than a wall of chips. */
    '      <div v-for="g in filteredOptions" :key="g.name" class="opt-group">',
    '        <h4 class="opt-group__name">{{ g.name }}</h4>',
    '        <ul class="map-list">',
    '          <li v-for="o in g.options" :key="o.id" class="opt-row"',
    '            :class="{ \'is-open\': openOptionId === o.id, \'has-rule\': ruleOf(o).serviceId || ruleStaff(o).length }">',
    '            <button type="button" class="opt-row__head" @click="toggleOption(o.id)"',
    '              :aria-expanded="openOptionId === o.id ? \'true\' : \'false\'">',
    '              <span class="map-row__name">{{ o.name }}</span>',
    '              <span class="opt-row__sum">{{ optionSummary(o) }}</span>',
    '              <span class="opt-row__edit">{{ openOptionId === o.id ? "Close" : "Change" }}</span>',
    '            </button>',
    '            <div v-if="openOptionId === o.id" class="opt-row__rules">',
    '              <div class="opt-rule">',
    '                <span class="opt-rule__label">Booked with it, the service counts as</span>',
    '                <select class="input" :value="ruleOf(o).serviceId || \'\'"',
    '                  @change="setOptionRuleService(o, $event.target.value || null)">',
    '                  <option value="">Whatever it was booked as</option>',
    '                  <option v-for="svc in services" :key="svc.id" :value="svc.id">{{ svc.name }}</option>',
    '                </select>',
    '              </div>',
    /* Offered on more than one kind of service — Gel Polish on manicures and
       pedicures — it can mean something different on each. A row per turn
       type it is booked on; blank follows the answer above. */
    '              <div v-if="optionBases(o).length > 1" class="opt-rule">',
    '                <span class="opt-rule__label">Or, depending on what it is added to</span>',
    '                <div v-for="b in optionBases(o)" :key="b.id" class="opt-rule__base">',
    '                  <span class="opt-rule__base-name">On a {{ b.name }}</span>',
    '                  <select class="input" :value="ruleBase(o, b.id)"',
    '                    @change="setOptionRuleBase(o, b.id, $event.target.value || null)">',
    '                    <option value="">As above</option>',
    '                    <option v-for="svc in services" :key="svc.id" :value="svc.id">{{ svc.name }}</option>',
    '                  </select>',
    '                </div>',
    '              </div>',
    '              <div class="opt-rule">',
    '                <span class="opt-rule__label">Only these technicians</span>',
    '                <div class="map-row__pick">',
    '                  <button v-for="s in roster" :key="s.id" type="button" class="chip chip--sm"',
    '                    :class="{ \'chip--on\': ruleStaff(o).indexOf(s.id) !== -1 }"',
    '                    @click="toggleOptionRuleStaff(o, s.id)">',
    '                    <span class="chip__name">{{ s.name }}</span>',
    '                  </button>',
    '                </div>',
    '                <p v-if="!ruleStaff(o).length" class="opt-rule__hint">Anyone can take it.</p>',
    '              </div>',
    '            </div>',
    '          </li>',
    '        </ul>',
    '      </div>',
    '    </template>',
    '  </template>',
    '</div>'
  ].join('\n')
};
